// Supabase Configuration
const SUPABASE_URL = 'https://nseykgyfbakvthrymuoe.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5zZXlrZ3lmYmFrdnRocnltdW9lIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY4NDU3MjksImV4cCI6MjA5MjQyMTcyOX0.zXkjvZb02qXCPl9nDSl-M64mfpOswhBsKFp_phZcOzA';

/**
 * Returns a YYYY-MM-DD string in the user's LOCAL timezone.
 * Using toISOString() would give UTC which is wrong for Bangladesh (UTC+6) —
 * before 6 AM local time the UTC date is still the previous day.
 * @param {Date} date
 * @returns {string}
 */
const localDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Save today's Division-02 data to Supabase as yesterday's data
 * @param {Array} areaReports - Array of area data with name and collectedQty
 * @returns {Promise<boolean>} - Success status
 */
export const saveTodayData = async (areaReports) => {
  try {
    // Save as yesterday's date for tomorrow's comparison
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = localDateStr(yesterday); // YYYY-MM-DD in local timezone
    
    const records = areaReports.map(area => ({
      date: yesterdayStr,
      area_name: area.name,
      collected_qty: parseInt(area.collectedQty) || 0,
    }));

    const response = await fetch(`${SUPABASE_URL}/rest/v1/division02_daily`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Prefer': 'return=minimal',
      },
      body: JSON.stringify(records),
    });

    return response.ok;
  } catch (error) {
    console.error('Failed to save data to Supabase:', error);
    return false;
  }
};

/**
 * Get yesterday's Division-02 data from Supabase
 * @returns {Promise<Object>} - Object with area names as keys and collected_qty as values
 */
export const getYesterdayData = async () => {
  try {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = localDateStr(yesterday); // YYYY-MM-DD in local timezone

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/division02_daily?date=eq.${yesterdayStr}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      }
    );

    if (!response.ok) {
      return {};
    }

    const data = await response.json();
    
    // Convert array to object for easy lookup
    const yesterdayData = {};
    data.forEach(record => {
      yesterdayData[record.area_name] = record.collected_qty;
    });

    return yesterdayData;
  } catch (error) {
    console.error('Failed to fetch yesterday data from Supabase:', error);
    return {};
  }
};

/**
 * Save Tangail plaza data to Supabase as yesterday's data
 * @param {Array} plazaReports - Array of plaza data with name and collectedQty
 * @returns {Promise<boolean>} - Success status
 */
export const saveTangailPlazaData = async (plazaReports) => {
  try {
    // Save as yesterday's date for tomorrow's comparison
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = localDateStr(yesterday); // YYYY-MM-DD in local timezone
    
    const records = plazaReports.map(plaza => ({
      date: yesterdayStr,
      plaza_name: plaza.name,
      collected_qty: parseInt(plaza.collectedQty) || 0,
    }));

    const response = await fetch(`${SUPABASE_URL}/rest/v1/tangail_plaza_daily`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Prefer': 'return=minimal',
      },
      body: JSON.stringify(records),
    });

    return response.ok;
  } catch (error) {
    console.error('Failed to save Tangail plaza data to Supabase:', error);
    return false;
  }
};

/**
 * Get yesterday's Tangail plaza data from Supabase
 * @returns {Promise<Object>} - Object with plaza names as keys and collected_qty as values
 */
export const getYesterdayTangailPlazaData = async () => {
  try {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = localDateStr(yesterday); // YYYY-MM-DD in local timezone

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/tangail_plaza_daily?date=eq.${yesterdayStr}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      }
    );

    if (!response.ok) {
      return {};
    }

    const data = await response.json();
    
    // Convert array to object for easy lookup
    const yesterdayData = {};
    data.forEach(record => {
      yesterdayData[record.plaza_name] = record.collected_qty;
    });

    return yesterdayData;
  } catch (error) {
    console.error('Failed to fetch yesterday Tangail plaza data from Supabase:', error);
    return {};
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// ALL-AREA universal plaza daily collection
// Table: all_plaza_daily
//   date          DATE        (YYYY-MM-DD)
//   area_name     TEXT
//   plaza_name    TEXT
//   collected_qty INTEGER
//   PRIMARY KEY (date, area_name, plaza_name)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Save all-area plaza collection data to Supabase.
 * Call this on every file upload so that tomorrow's upload can compare.
 *
 * @param {Array<{area: string, plaza: string, collectedQty: number}>} plazaRecords
 * @returns {Promise<boolean>}
 */
export const saveAllPlazaDailyCollection = async (plazaRecords) => {
  if (!plazaRecords || plazaRecords.length === 0) return false;

  try {
    // Always store as yesterday's date so that tomorrow's upload fetch
    // (which queries yesterday) finds this data immediately.
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = localDateStr(yesterday); // YYYY-MM-DD in local timezone

    // Step 1: Delete ALL existing rows for yesterday — full replacement.
    const deleteResp = await fetch(
      `${SUPABASE_URL}/rest/v1/all_plaza_daily?date=eq.${yesterdayStr}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'return=minimal',
        },
      }
    );

    if (!deleteResp.ok) {
      const text = await deleteResp.text();
      console.error('saveAllPlazaDailyCollection delete error:', text);
      return false;
    }

    // Step 2: Insert fresh records with yesterday's date.
    const records = plazaRecords.map(p => ({
      date: yesterdayStr,
      area_name: p.area,
      plaza_name: p.plaza,
      collected_qty: parseInt(p.collectedQty) || 0,
    }));

    const insertResp = await fetch(
      `${SUPABASE_URL}/rest/v1/all_plaza_daily`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify(records),
      }
    );

    if (!insertResp.ok) {
      const text = await insertResp.text();
      console.error('saveAllPlazaDailyCollection insert error:', text);
    }
    return insertResp.ok;
  } catch (error) {
    console.error('Failed to save all-plaza daily data:', error);
    return false;
  }
};

/**
 * Fetch yesterday's all-area plaza collection from Supabase.
 * Returns a nested lookup: { [areaName]: { [plazaName]: collectedQty } }
 *
 * @returns {Promise<Object>}
 */
export const getYesterdayAllPlazaCollection = async () => {
  try {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = localDateStr(yesterday);

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/all_plaza_daily?date=eq.${yesterdayStr}&select=area_name,plaza_name,collected_qty`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      }
    );

    if (!response.ok) return {};

    const data = await response.json();

    // Build nested lookup  { area -> { plaza -> qty } }
    const result = {};
    data.forEach(record => {
      if (!result[record.area_name]) result[record.area_name] = {};
      result[record.area_name][record.plaza_name] = record.collected_qty;
    });
    return result;
  } catch (error) {
    console.error('Failed to fetch yesterday all-plaza data:', error);
    return {};
  }
};

/**
 * Convenience flat-lookup version for a single area.
 * Returns { [plazaName]: collectedQty } — mirrors getYesterdayTangailPlazaData shape.
 *
 * @param {string} areaName
 * @returns {Promise<Object>}
 */
export const getYesterdayPlazaCollectionForArea = async (areaName) => {
  try {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = localDateStr(yesterday);

    const encoded = encodeURIComponent(areaName);
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/all_plaza_daily?date=eq.${yesterdayStr}&area_name=eq.${encoded}&select=plaza_name,collected_qty`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      }
    );

    if (!response.ok) return {};

    const data = await response.json();
    const result = {};
    data.forEach(record => {
      result[record.plaza_name] = record.collected_qty;
    });
    return result;
  } catch (error) {
    console.error(`Failed to fetch yesterday plaza data for area "${areaName}":`, error);
    return {};
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// TANGAIL PLAZA TARGETS
// Table: tangail_plaza_targets
//   plaza_name   TEXT PRIMARY KEY
//   target_qty   INTEGER DEFAULT 0
//   updated_at   TIMESTAMPTZ
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch daily targets for all Tangail plazas.
 * Returns { [plaza_name]: target_qty }
 *
 * @returns {Promise<Object>}
 */
export const getTangailPlazaTargets = async () => {
  try {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/tangail_plaza_targets?select=plaza_name,target_qty`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      }
    );

    if (!response.ok) return {};

    const data = await response.json();
    const result = {};
    data.forEach(r => {
      result[r.plaza_name] = r.target_qty;
    });
    return result;
  } catch (error) {
    console.error('Failed to fetch Tangail plaza targets:', error);
    return {};
  }
};

/**
 * Upsert daily targets for Tangail plazas (admin only).
 * Existing rows are updated via ON CONFLICT resolution.
 *
 * @param {Array<{plaza_name: string, target_qty: number}>} targets
 * @returns {Promise<boolean>}
 */
export const saveTangailPlazaTargets = async (targets) => {
  if (!targets || targets.length === 0) return false;

  try {
    const records = targets.map(t => ({
      plaza_name: t.plaza_name,
      target_qty: parseInt(t.target_qty) || 0,
      updated_at: new Date().toISOString(),
    }));

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/tangail_plaza_targets`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'resolution=merge-duplicates,return=minimal',
        },
        body: JSON.stringify(records),
      }
    );

    if (!response.ok) {
      const text = await response.text();
      console.error('saveTangailPlazaTargets error:', text);
    }
    return response.ok;
  } catch (error) {
    console.error('Failed to save Tangail plaza targets:', error);
    return false;
  }
};

// Table: tangail_area_monthly_pct
// Stores the "Previous Month Qty %" baseline per area (one snapshot, editable).

/**
 * Get stored Previous Month Qty % per area.
 * @returns {Promise<Object>} - { area_name: qty_percent }
 */
export const getTangailAreaMonthlyPct = async () => {
  try {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/tangail_area_monthly_pct?select=area_name,qty_percent`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      }
    );

    if (!response.ok) return {};

    const data = await response.json();
    const result = {};
    data.forEach(r => {
      result[r.area_name] = Number(r.qty_percent);
    });
    return result;
  } catch (error) {
    console.error('Failed to fetch Tangail area monthly pct:', error);
    return {};
  }
};

/**
 * Upsert the Previous Month Qty % per area.
 * @param {Array<{area_name: string, qty_percent: number}>} records
 * @returns {Promise<boolean>}
 */
export const saveTangailAreaMonthlyPct = async (records) => {
  if (!records || records.length === 0) return false;

  try {
    const payload = records.map(r => ({
      area_name: r.area_name,
      qty_percent: Number(r.qty_percent) || 0,
      updated_at: new Date().toISOString(),
    }));

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/tangail_area_monthly_pct`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'resolution=merge-duplicates,return=minimal',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const text = await response.text();
      console.error('saveTangailAreaMonthlyPct error:', text);
    }
    return response.ok;
  } catch (error) {
    console.error('Failed to save Tangail area monthly pct:', error);
    return false;
  }
};
